import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import OcrUploadComponent from './OcrUploadComponent';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import { useFilePreview } from '../hooks/useFilePreview';
import { RESIDENCE_STATUS_OPTIONS } from '../config/residenceStatusConfig';
import { sortRenewalEntriesNewestFirst } from '../utils/residenceStatus';

// 可复用的表单项和详情项组件 - 横向布局（左边字段名，右边字段内容）
const DetailItem = ({ label, value }) => (
    <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
        <dt className="col-span-2 text-sm font-medium text-gray-500 flex items-center">{label}</dt>
        <dd className="col-span-3 text-sm text-gray-900 flex items-center">{value || 'N/A'}</dd>
    </div>
);
const EditItem = ({ label, name, value, onChange, type = 'text' }) => {
    // Set date range limits for date inputs to restrict year to 4 digits
    const dateProps = type === 'date' ? {
        min: '1900-01-01',
        max: '2099-12-31'
    } : {};

    return (
        <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
            <label htmlFor={name} className="col-span-2 text-sm font-medium text-gray-700 flex items-center">{label}</label>
            <div className="col-span-3">
                <input
                    type={type}
                    name={name}
                    id={name}
                    value={value || ''}
                    onChange={onChange}
                    {...dateProps}
                    className="block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
            </div>
        </div>
    );
};

const SelectItem = ({ label, name, value, onChange, options, t }) => (
    <div className="grid grid-cols-5 gap-4 py-3 border-b border-gray-100">
        <label htmlFor={name} className="col-span-2 text-sm font-medium text-gray-700 flex items-center">{label}</label>
        <div className="col-span-3">
            <select
                name={name}
                id={name}
                value={value || ''}
                onChange={onChange}
                className="block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
                <option value="" disabled>{t('pleaseSelect')}</option>
                {options.map(option => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        </div>
    </div>
);

const isImage = (fileType) => fileType.startsWith('image/');

const renewalApplicationTypeOptions = ["期間更新", "認定証明書申請", "資格変更"];
const renewalSubmissionMethodOptions = ["対面提出", "オンライン提出"];
const renewalSubmissionAgentOptions = ["スティーブン", "ナン", "ガン", "他"];
const submissionAgentFixedOptions = ["スティーブン", "ナン", "ガン"];

const getSubmissionAgentOptionValue = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';
    if (submissionAgentFixedOptions.includes(trimmed)) return trimmed;
    return '他';
};

export default function ResidenceInfoModal({ isOpen, onClose, talent, showBackButton = false, onBack, onSaveSuccess }) {
    const { t } = useTranslation();
    const attachmentPreview = useFilePreview(apiService.downloadAttachment);
    const [formData, setFormData] = useState({});
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]); // 临时选中的文件
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [thumbnailUrls, setThumbnailUrls] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [showOcrUpload, setShowOcrUpload] = useState(false);
    const [ocrData, setOcrData] = useState(null);
    const [renewalEntries, setRenewalEntries] = useState([]);

    // Photo display state (read-only)
    const [existingPhoto, setExistingPhoto] = useState(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);

    const fetchAttachments = useCallback(async (talentId) => {
        if (!talentId) return;
        try {
            const attachmentsData = await apiService.getAttachmentsForTalent(talentId);
            const residenceAttachments = attachmentsData.filter(att => att.sourceModule === "Residence");
            const photoAttachments = attachmentsData.filter(att => att.sourceModule === "Photo");

            setExistingAttachments(residenceAttachments);

            // Handle existing photo (read-only display)
            if (photoAttachments.length > 0) {
                const photo = photoAttachments[0]; // Use first photo
                setExistingPhoto(photo);
                // Load photo preview
                apiService.downloadAttachment(photo.id)
                    .then(blob => {
                        const url = URL.createObjectURL(blob);
                        setPhotoPreviewUrl(url);
                    })
                    .catch(err => console.error('Failed to load photo:', err));
            } else {
                setExistingPhoto(null);
                setPhotoPreviewUrl(null);
            }

            const newThumbnailUrls = {};
            for (const att of residenceAttachments) {
                if (isImage(att.fileType)) {
                    const blob = await apiService.downloadAttachment(att.id);
                    newThumbnailUrls[att.id] = URL.createObjectURL(blob);
                }
            }
            setThumbnailUrls(newThumbnailUrls);
        } catch (err) {
            setError(t('loadFailed') + ': ' + err.message);
        }
    }, [t]);

    const fetchResidenceData = useCallback(async () => {
        if (!talent?.id) return;
        setIsLoading(true);
        setError('');
        try {
            // 先尝试从API获取最新的在留信息
            const residenceData = await apiService.getResidenceByTalentId(talent.id);
            setFormData(residenceData || {});

            // 加载现有的更新履历
            if (residenceData?.renewalEntries && Array.isArray(residenceData.renewalEntries)) {
                setRenewalEntries(sortRenewalEntriesNewestFirst(residenceData.renewalEntries).map(entry => ({
                    id: entry.id || Date.now() + Math.random(),
                    renewalDate: entry.renewalDate || '',
                    renewalGrantDate: entry.renewalGrantDate || '',
                    renewalNumber: entry.renewalNumber || '',
                    residenceStatus: entry.residenceStatus || '',
                    renewalApplicationType: entry.renewalApplicationType || '',
                    submissionMethod: entry.submissionMethod || '',
                    submissionAgent: entry.submissionAgent || '',
                    createdAt: entry.createdAt || '',
                    submissionAgentOption: getSubmissionAgentOptionValue(entry.submissionAgent),
                    submissionAgentOther: submissionAgentFixedOptions.includes(entry.submissionAgent || '') ? '' : (entry.submissionAgent || '')
                })));
            } else {
                setRenewalEntries([]);
            }
        } catch (err) {
            // 如果获取失败，使用传入的数据
            console.warn('Cannot fetch latest residence info, using passed data:', err.message);
            setFormData(talent.residencePermit || {});
            setRenewalEntries([]);
        } finally {
            setIsLoading(false);
        }
    }, [talent]);

    useEffect(() => {
        if (isOpen && talent) {
            setFilesToUpload([]);
            setSelectedFiles([]);
            setError('');

            // Cleanup old photo URL before fetching new talent
            const oldPhotoUrl = photoPreviewUrl;
            if (oldPhotoUrl) {
                URL.revokeObjectURL(oldPhotoUrl);
            }
            setPhotoPreviewUrl(null);
            setExistingPhoto(null);

            fetchResidenceData();
            fetchAttachments(talent.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, talent?.id]);

    // Cleanup thumbnails when component unmounts or thumbnails change
    useEffect(() => {
        return () => {
            Object.values(thumbnailUrls).forEach(URL.revokeObjectURL);
        };
    }, [thumbnailUrls]);

    // Cleanup photo preview URL on unmount
    useEffect(() => {
        return () => {
            if (photoPreviewUrl) {
                URL.revokeObjectURL(photoPreviewUrl);
            }
        };
    }, [photoPreviewUrl]);

    // Photo download handler
    const handleDownloadPhoto = async () => {
        if (existingPhoto) {
            try {
                const blob = await apiService.downloadAttachment(existingPhoto.id);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = existingPhoto.fileName || 'photo.jpg';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                setError(t('downloadFailed') + ': ' + err.message);
            }
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        setError('');
        try {
            // Helper function to normalize date format to YYYY-MM-DD
            const normalizeDate = (dateValue) => {
                if (!dateValue || dateValue === '') return null;
                if (typeof dateValue === 'string') {
                    const trimmed = dateValue.trim();
                    if (trimmed === '') return null;
                    // Extract YYYY-MM-DD format regardless of original format
                    const cleanDate = trimmed.split('T')[0];
                    return cleanDate && cleanDate !== '' ? cleanDate : null;
                }
                return null;
            };

            // Update formData with current timestamp for ResidenceInfoUpdatedAt and clean renewal entries
            const cleanRenewalEntries = renewalEntries.map(entry => ({
                renewalDate: normalizeDate(entry.renewalDate),
                renewalGrantDate: normalizeDate(entry.renewalGrantDate),
                renewalNumber: entry.renewalNumber || '',
                residenceStatus: entry.residenceStatus || '',
                renewalApplicationType: entry.renewalApplicationType || '',
                submissionMethod: entry.submissionMethod || '',
                submissionAgent: entry.submissionAgent || ''
            }));

            const updatedFormData = {
                ...formData,
                // Normalize all date fields to YYYY-MM-DD format
                applicationDate: normalizeDate(formData.applicationDate),
                grantDate: normalizeDate(formData.grantDate),
                interviewDate: normalizeDate(formData.interviewDate),
                residenceCardExpiry: normalizeDate(formData.residenceCardExpiry),
                passportExpiry: normalizeDate(formData.passportExpiry),
                startDate: normalizeDate(formData.startDate),
                endDate: normalizeDate(formData.endDate),
                renewalDate: normalizeDate(formData.renewalDate),
                renewalGrantDate: normalizeDate(formData.renewalGrantDate),
                residenceInfoUpdatedAt: new Date().toISOString(),
                renewalEntries: cleanRenewalEntries
            };

            // Remove navigation properties and non-data fields
            delete updatedFormData.talent;
            delete updatedFormData.residenceStaff;
            delete updatedFormData.remainingDays;

            await apiService.createOrUpdateResidence(talent.id, updatedFormData);

            if (filesToUpload.length > 0) {
                for (const file of filesToUpload) {
                    await apiService.uploadAttachment(talent.id, file, "Residence");
                }
            }

            // Show success toast/alert
            alert('保存しました');

            // Notify parent component that save was successful
            if (onSaveSuccess) {
                onSaveSuccess();
            }

            // Auto-redirect back to talent list page
            setTimeout(() => {
                onClose();
            }, 500);
        } catch(e) {
            setError(e.message || t('saveFailed'));
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddRenewal = () => {
        const newEntry = {
            id: Date.now(), // Simple unique ID
            renewalDate: '',
            renewalGrantDate: '',
            renewalNumber: '',
            residenceStatus: '',
            renewalApplicationType: '',
            submissionMethod: '',
            submissionAgent: '',
            submissionAgentOption: '',
            submissionAgentOther: ''
        };
        setRenewalEntries(prev => [newEntry, ...prev]);
    };

    const handleRenewalChange = (id, field, value) => {
        setRenewalEntries(renewalEntries.map(entry =>
            entry.id === id ? { ...entry, [field]: value } : entry
        ));
    };

    const handleSubmissionAgentOptionChange = (id, value) => {
        setRenewalEntries(renewalEntries.map(entry => {
            if (entry.id !== id) return entry;
            if (value === '他') {
                const otherValue = entry.submissionAgentOther || '';
                return {
                    ...entry,
                    submissionAgentOption: '他',
                    submissionAgent: otherValue
                };
            }
            return {
                ...entry,
                submissionAgentOption: value,
                submissionAgentOther: '',
                submissionAgent: value
            };
        }));
    };

    const handleSubmissionAgentOtherChange = (id, value) => {
        setRenewalEntries(renewalEntries.map(entry =>
            entry.id === id ? {
                ...entry,
                submissionAgentOption: '他',
                submissionAgentOther: value,
                submissionAgent: value
            } : entry
        ));
    };

    const handleRemoveRenewal = (id) => {
        setRenewalEntries(renewalEntries.filter(entry => entry.id !== id));
    };

    const handleFileChange = (e) => {
        // 只是暂时保存选中的文件，不直接添加到待上传列表
        setSelectedFiles([...e.target.files]);
        e.target.value = '';
    };

    const handleAddFiles = () => {
        // 点击"添加"按钮后，将选中的文件添加到待上传列表
        if (selectedFiles.length > 0) {
            setFilesToUpload([...filesToUpload, ...selectedFiles]);
            setSelectedFiles([]); // 清空临时选择
            // 重置file input
            const fileInput = document.getElementById('file-input-residence');
            if (fileInput) fileInput.value = '';
        }
    };

    const handleRemovePendingFile = (index) => {
        // 从待上传列表中删除文件
        setFilesToUpload(filesToUpload.filter((_, i) => i !== index));
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (window.confirm(t('confirmDelete'))) {
            try {
                await apiService.deleteAttachment(attachmentId);
                fetchAttachments(talent.id); // Refresh attachments list
            } catch (err) {
                setError(t('deleteFailed') + ': ' + err.message);
            }
        }
    };

    const handleDownload = async (attachmentId, fileName) => {
        try {
            const blob = await apiService.downloadAttachment(attachmentId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url); // Clean up the object URL
        } catch (err) {
            setError(err.message || t('downloadFailed'));
        }
    };

    const handleOcrResult = (result) => {
        if (result.success && result.data) {
            // OCRで読み取ったデータをフォームに反映
            const newFormData = { ...formData };
            
            if (result.data.applicationDate) newFormData.applicationDate = result.data.applicationDate;
            if (result.data.grantDate) newFormData.grantDate = result.data.grantDate;
            if (result.data.applicationMethod) newFormData.applicationMethod = result.data.applicationMethod;
            if (result.data.applicationNumber) newFormData.applicationNumber = result.data.applicationNumber;
            if (result.data.applicant) newFormData.applicant = result.data.applicant;
            if (result.data.residenceCardNumber) newFormData.residenceCardNumber = result.data.residenceCardNumber;
            if (result.data.residenceCardExpiry) newFormData.residenceCardExpiry = result.data.residenceCardExpiry;
            if (result.data.passportNumber) newFormData.passportNumber = result.data.passportNumber;
            if (result.data.passportExpiry) newFormData.passportExpiry = result.data.passportExpiry;
            if (result.data.status) newFormData.status = result.data.status;
            if (result.data.interviewDate) newFormData.interviewDate = result.data.interviewDate;
            if (result.data.startDate) newFormData.startDate = result.data.startDate;
            if (result.data.endDate) newFormData.endDate = result.data.endDate;
            if (result.data.renewalDate) newFormData.renewalDate = result.data.renewalDate;
            if (result.data.renewalGrantDate) newFormData.renewalGrantDate = result.data.renewalGrantDate;

            setFormData(newFormData);
            setOcrData(result);
            setShowOcrUpload(false);

            alert(`OCR読み取りが完了しました。信頼度: ${(result.confidence * 100).toFixed(1)}%\n読み取り結果を確認してください。`);
        }
    };

    const handleStartOcr = () => {
        setShowOcrUpload(true);
    };

    const handleCancelOcr = () => {
        setShowOcrUpload(false);
    };

    if (!isOpen) return null;

    const renderSalesInfo = () => (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">個人情報（参考用・編集不可）</h3>
            <div className="flex">
                {/* 左側个人信息 */}
                <div className="flex-1">
                    <DetailItem label="氏名（フルネーム）" value={talent.fullName} />
                    <DetailItem label="氏名（フリガナ）" value={talent.fullNameFurigana} />
                    <DetailItem label="電話番号" value={talent.phoneNumber} />
                    <DetailItem label="住所" value={talent.address} />
                    <DetailItem label="国籍" value={talent.nationality} />
                    <DetailItem label="生年月日" value={talent.dateOfBirth} />
                    <DetailItem label="性別" value={talent.gender} />
                    <DetailItem label="分野" value={talent.field} />
                    <DetailItem label="進歩状況" value={talent.employmentStatus} />
                    <DetailItem label="所属機関名" value={talent.affiliatedOrganizationName} />
                    <DetailItem label="所属機関電話" value={talent.affiliatedOrganizationPhone} />
                    <DetailItem label="所属機関Email" value={talent.affiliatedOrganizationEmail} />
                    <DetailItem label="所属機関担当者" value={talent.affiliatedOrganizationContact} />
                    <DetailItem label="事業所名" value={talent.workplaceName} />
                    <DetailItem label="事業所住所" value={talent.workplaceAddress} />
                    <DetailItem label="事業所電話" value={talent.workplacePhone} />
                    <DetailItem label="事業所Email" value={talent.workplaceEmail} />
                    <DetailItem label="事業所担当者" value={talent.workplaceContact} />
                    <DetailItem label="事業所担当者役職" value={talent.workplaceContactTitle} />
                </div>

                {/* 右侧照片区域 - 与氏名字段对齐 */}
                <div className="ml-6 flex flex-col items-center space-y-2">
                    {/* 写真プレビューエリア - 3cm x 4cm */}
                    <div
                        className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden"
                        style={{ width: '113px', height: '151px' }}
                    >
                        {photoPreviewUrl ? (
                            <img
                                src={photoPreviewUrl}
                                alt="人材写真"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="text-center p-2">
                                <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <p className="text-xs text-gray-500">写真なし</p>
                            </div>
                        )}
                    </div>

                    {/* Size information */}
                    <p className="text-xs text-gray-600">3cm × 4cm</p>

                    {/* Photo download button */}
                    <div className="flex space-x-2">
                        <button
                            type="button"
                            className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                            disabled
                        >
                            アップロード
                        </button>
                        {existingPhoto && (
                            <button
                                type="button"
                                onClick={() => attachmentPreview.openPreview(existingPhoto.id, existingPhoto.fileName || 'photo.jpg', existingPhoto.fileType)}
                                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors"
                            >
                                {t('viewOnline')}
                            </button>
                        )}
                        {photoPreviewUrl && (
                            <button
                                type="button"
                                onClick={handleDownloadPhoto}
                                className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 transition-colors"
                            >
                                ダウンロード
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAttachments = (canDelete = false) => (
        <div className="md:col-span-3 border-t pt-4 mt-2">
            <h4 className="font-semibold text-gray-800">{t('attachments')}</h4>

            {/* ファイル選択と追加ボタン */}
            {canDelete && (
                <div className="mb-4 mt-2">
                    <div className="flex items-center gap-2">
                        <label className="inline-flex cursor-pointer items-center justify-center rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-100">
                            <input
                                id="file-input-residence"
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            {t('selectFiles')}
                        </label>
                        <span className="min-w-0 flex-1 truncate text-sm text-gray-500">
                            {selectedFiles.length > 0 ? t('selectedFilesCount', { count: selectedFiles.length }) : t('noFilesSelected')}
                        </span>
                        <button
                            type="button"
                            onClick={handleAddFiles}
                            disabled={selectedFiles.length === 0}
                            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            追加
                        </button>
                    </div>
                    {selectedFiles.length > 0 && (
                        <p className="text-xs text-gray-600 mt-1">選択中: {selectedFiles.length}個のファイル</p>
                    )}
                </div>
            )}

            {/* アップロード予定ファイル一覧 */}
            {filesToUpload.length > 0 && (
                <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">追加予定のファイル:</h5>
                    <ul className="space-y-1">
                        {filesToUpload.map((file, index) => (
                            <li key={index} className="flex items-center justify-between py-2 px-3 bg-yellow-50 border border-yellow-200 rounded">
                                <div className="flex items-center">
                                    <span className="text-2xl mr-2">📎</span>
                                    <span className="text-sm text-gray-700">{file.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({(file.size / 1024).toFixed(1)} KB)</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRemovePendingFile(index)}
                                    className="text-red-500 hover:text-red-700 text-sm"
                                >
                                    削除
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* アップロード済み添付ファイル一覧 */}
            <h5 className="text-sm font-medium text-gray-700 mb-2">アップロード済みファイル:</h5>
            {existingAttachments.length > 0 ? (
                <ul className="mt-2">
                    {existingAttachments.map(att => (
                        <li key={att.id} className="flex items-center justify-between py-2 border-b">
                            <div className="flex items-center">
                                {isImage(att.fileType) && thumbnailUrls[att.id] ? (
                                    <img src={thumbnailUrls[att.id]} alt={att.fileName} className="w-16 h-16 object-cover mr-4 rounded" />
                                ) : (
                                    <span className="text-4xl mr-4">📄</span>
                                )}
                                <span>{att.fileName}</span>
                            </div>
                            <div>
                                <button type="button" onClick={() => attachmentPreview.openPreview(att.id, att.fileName, att.fileType)} className="text-blue-500 hover:underline mr-4">{t('viewOnline')}</button>
                                <button type="button" onClick={() => handleDownload(att.id, att.fileName)} className="text-blue-500 hover:underline mr-4">{t('download')}</button>
                                {canDelete && (
                                    <button type="button" onClick={() => handleDeleteAttachment(att.id)} className="text-red-500 hover:underline">{t('delete')}</button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-gray-500">{t('none')}</p>
            )}
        </div>
    );

    const renderEditMode = () => (
        <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
            <h3 className="text-lg font-medium text-blue-900 mb-4">在留情報</h3>

            {/* 面接日を最初に配置 */}
            <EditItem label={t('interviewDate')} name="interviewDate" type="date" value={formData.interviewDate?.split('T')[0]} onChange={handleChange} />
            <EditItem label={t('applicationDate')} name="applicationDate" type="date" value={formData.applicationDate?.split('T')[0]} onChange={handleChange} />
            <EditItem label={t('grantDate')} name="grantDate" type="date" value={formData.grantDate?.split('T')[0]} onChange={handleChange} />
            <EditItem label={t('applicationMethod')} name="applicationMethod" value={formData.applicationMethod} onChange={handleChange} />
            <EditItem label={t('applicationNumber')} name="applicationNumber" value={formData.applicationNumber} onChange={handleChange} />
            <SelectItem
                label="在留資格"
                name="residenceStatus"
                value={formData.residenceStatus}
                onChange={handleChange}
                options={RESIDENCE_STATUS_OPTIONS}
                t={t}
            />
            <SelectItem
                label="申請"
                name="applicationType"
                value={formData.applicationType}
                onChange={handleChange}
                options={renewalApplicationTypeOptions}
                t={t}
            />
            <EditItem label={t('applicant')} name="applicant" value={formData.applicant} onChange={handleChange} />
            <EditItem label={t('residenceCardNumber')} name="residenceCardNumber" value={formData.residenceCardNumber} onChange={handleChange} />
            <EditItem label={t('residenceCardExpiry')} name="residenceCardExpiry" type="date" value={formData.residenceCardExpiry?.split('T')[0]} onChange={handleChange} />
            <EditItem label={t('passportNumber')} name="passportNumber" value={formData.passportNumber} onChange={handleChange} />
            <EditItem label={t('passportExpiry')} name="passportExpiry" type="date" value={formData.passportExpiry?.split('T')[0]} onChange={handleChange} />
            <EditItem label={t('startDate')} name="startDate" type="date" value={formData.startDate?.split('T')[0]} onChange={handleChange} />
            <EditItem label={t('endDate')} name="endDate" type="date" value={formData.endDate?.split('T')[0]} onChange={handleChange} />

            {/* 動的な更新セクション */}
            <div className="py-3 border-b border-gray-100">
                <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium text-gray-700">更新履歴</h4>
                    <button
                        type="button"
                        onClick={handleAddRenewal}
                        className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        追加更新
                    </button>
                </div>
                {renewalEntries.map((entry) => (
                    <div key={entry.id} className="border border-gray-200 rounded p-3 mb-2 relative">
                        <button
                            type="button"
                            onClick={() => handleRemoveRenewal(entry.id)}
                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm"
                        >
                            ✕
                        </button>
                        <EditItem
                            label="更新日"
                            name={`renewalDate_${entry.id}`}
                            type="date"
                            value={entry.renewalDate}
                            onChange={(e) => handleRenewalChange(entry.id, 'renewalDate', e.target.value)}
                        />
                        <EditItem
                            label="更新交付日"
                            name={`renewalGrantDate_${entry.id}`}
                            type="date"
                            value={entry.renewalGrantDate}
                            onChange={(e) => handleRenewalChange(entry.id, 'renewalGrantDate', e.target.value)}
                        />
                        <EditItem
                            label="更新番号"
                            name={`renewalNumber_${entry.id}`}
                            value={entry.renewalNumber}
                            onChange={(e) => handleRenewalChange(entry.id, 'renewalNumber', e.target.value)}
                        />
                        <SelectItem
                            label="在留資格"
                            name={`residenceStatus_${entry.id}`}
                            value={entry.residenceStatus}
                            onChange={(e) => handleRenewalChange(entry.id, 'residenceStatus', e.target.value)}
                            options={RESIDENCE_STATUS_OPTIONS}
                            t={t}
                        />
                        <SelectItem
                            label="申請"
                            name={`renewalApplicationType_${entry.id}`}
                            value={entry.renewalApplicationType}
                            onChange={(e) => handleRenewalChange(entry.id, 'renewalApplicationType', e.target.value)}
                            options={renewalApplicationTypeOptions}
                            t={t}
                        />
                        <SelectItem
                            label="提出方法"
                            name={`submissionMethod_${entry.id}`}
                            value={entry.submissionMethod}
                            onChange={(e) => handleRenewalChange(entry.id, 'submissionMethod', e.target.value)}
                            options={renewalSubmissionMethodOptions}
                            t={t}
                        />
                        <SelectItem
                            label="提出(取次者)"
                            name={`submissionAgent_${entry.id}`}
                            value={entry.submissionAgentOption || getSubmissionAgentOptionValue(entry.submissionAgent)}
                            onChange={(e) => handleSubmissionAgentOptionChange(entry.id, e.target.value)}
                            options={renewalSubmissionAgentOptions}
                            t={t}
                        />
                        {(entry.submissionAgentOption || getSubmissionAgentOptionValue(entry.submissionAgent)) === '他' && (
                            <EditItem
                                label="提出(取次者) 他"
                                name={`submissionAgentOther_${entry.id}`}
                                value={entry.submissionAgentOther || ''}
                                onChange={(e) => handleSubmissionAgentOtherChange(entry.id, e.target.value)}
                            />
                        )}
                    </div>
                ))}
            </div>

            {renderAttachments(true)}
        </div>
    );

    return (
        <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl">
                <div className="p-6 flex justify-between items-start border-b">
                    <div className="flex flex-col">
                        <h2 className="text-2xl font-bold">{talent.fullName} - {t('residenceInfo')}</h2>
                        {!showOcrUpload && (
                            <div className="flex space-x-2 mt-2">
                                <button
                                    onClick={handleStartOcr}
                                    className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200 flex items-center"
                                >
                                    📷 OCR識別
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                    {isLoading ? (
                        <p>{t('loading')}</p>
                    ) : showOcrUpload ? (
                        <OcrUploadComponent
                            onOcrResult={handleOcrResult}
                            onCancel={handleCancelOcr}
                        />
                    ) : (
                        <>
                            {renderSalesInfo()}
                            {renderEditMode()}
                        </>
                    )}

                    {/* OCR読み取り結果メッセージ */}
                    {ocrData && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                            <p className="text-sm text-green-700">
                                OCRで読み取った内容を反映しました（信頼度: {(ocrData.confidence * 100).toFixed(1)}%）
                                <br />各項目を確認し、必要に応じて修正してください。
                            </p>
                        </div>
                    )}
                </div>
                {error && <p className="px-6 pb-4 text-sm text-red-600">{error}</p>}
                <div className="bg-gray-50 px-6 py-3 flex justify-between">
                    <div>
                        {showBackButton && (
                            <button
                                onClick={onBack}
                                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                            >
                                ← {t('backToList')}
                            </button>
                        )}
                    </div>
                    <div className="flex space-x-4">
                        <button onClick={handleSave} disabled={isLoading} className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400">
                            {isLoading ? t('saving') : '保存'}
                        </button>
                        <button onClick={onClose} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50">
                            {t('close')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <AttachmentPreviewModal
            previewState={attachmentPreview.previewState}
            onClose={attachmentPreview.closePreview}
        />
        </>
    );
}
